import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle, Search, MessageCircle, Book, Zap } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: 'general' | 'ai' | 'billing' | 'technical';
}

const faqData: FAQItem[] = [
  {
    id: '1',
    category: 'general',
    question: 'What is Octify and how does it work?',
    answer: 'Octify is an AI-powered relationship improvement platform that uses Gemini 2.5 Flash to analyze your interactions, mood patterns, and communication styles. It provides personalized insights, VR role-play scenarios, and actionable strategies to help you build stronger, more meaningful relationships with family, friends, colleagues, and even pets.'
  },
  {
    id: '2',
    category: 'ai',
    question: 'How accurate are the AI insights?',
    answer: 'Our AI insights are based on proven relationship psychology research and patterns from millions of anonymized interactions. While highly accurate for identifying trends and providing guidance, AI suggestions should be used as tools for reflection alongside your own judgment. For serious relationship or mental health concerns, we always recommend consulting with professional counselors.'
  },
  {
    id: '3',
    category: 'billing',
    question: 'Can I cancel my subscription anytime?',
    answer: 'Yes! You can cancel your subscription at any time from your account settings. Cancellation takes effect at the end of your current billing cycle, and you\'ll retain access to all premium features until then. We also offer a 14-day free trial for new users to explore all features risk-free.'
  },
  {
    id: '4',
    category: 'technical',
    question: 'Which wearable devices are supported?',
    answer: 'Octify integrates with Apple Watch, Fitbit, and any device compatible with Apple HealthKit or Google Fit. We analyze heart rate variability patterns, sleep quality, and stress indicators to correlate with your mood logs and relationship insights. Note: We never store raw biometric data, only anonymized correlations.'
  },
  {
    id: '5',
    category: 'general',
    question: 'Is my data private and secure?',
    answer: 'Absolutely. We use end-to-end encryption for all personal data, never sell your information, and follow strict GDPR compliance. You can access, modify, or delete your data anytime. Our AI training uses only anonymized, aggregated patterns - never your personal conversations or details.'
  },
  {
    id: '6',
    category: 'ai',
    question: 'What makes Octify different from other relationship apps?',
    answer: 'Octify uniquely combines AI-powered insights with VR role-play scenarios, wearable integration, and support for all relationship types (family, work, pets, friends). Our Gemini 2.5 Flash integration provides more nuanced, context-aware advice than traditional relationship apps, while our community features offer peer support and expert-led micro-courses.'
  },
  {
    id: '7',
    category: 'technical',
    question: 'Do I need VR equipment to use Octify?',
    answer: 'No! While VR role-play scenarios provide an immersive experience, all core features work perfectly on your phone, tablet, or computer. VR is an optional enhancement available through compatible headsets like Meta Quest, but you can practice conversations and access all insights through our standard interface.'
  },
  {
    id: '8',
    category: 'billing',
    question: 'What\'s included in the family plan?',
    answer: 'Our Premium plan includes family sharing for up to 4 members, each with their own private account and insights. Family members can optionally share progress milestones and participate in group challenges, but all personal data and AI conversations remain completely private to each individual.'
  }
];

const categories = [
  { id: 'all', label: 'All Questions', icon: HelpCircle, color: 'from-purple-500 to-indigo-500' },
  { id: 'general', label: 'General', icon: MessageCircle, color: 'from-blue-500 to-cyan-500' },
  { id: 'ai', label: 'AI Features', icon: Zap, color: 'from-orange-500 to-pink-500' },
  { id: 'billing', label: 'Billing', icon: Book, color: 'from-green-500 to-teal-500' },
  { id: 'technical', label: 'Technical', icon: HelpCircle, color: 'from-gray-500 to-gray-600' }
];

interface FAQProps {
  onContactSupport?: () => void;
}

export const FAQ: React.FC<FAQProps> = ({ onContactSupport }) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const prefersReducedMotion = useReducedMotion();

  const filteredFAQs = faqData.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleExpanded = (id: string) => {
    setExpandedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-50 to-teal-100 dark:from-purple-900/20 dark:via-blue-900/20 dark:to-teal-900/20 pt-20">
      {/* Structured Data for FAQ */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqData.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })}
      </script>

      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute inset-0 opacity-30"
          animate={prefersReducedMotion ? {} : {
            background: [
              "radial-gradient(circle at 20% 80%, #8B5CF6 0%, transparent 50%)",
              "radial-gradient(circle at 80% 20%, #06B6D4 0%, transparent 50%)",
              "radial-gradient(circle at 40% 40%, #10B981 0%, transparent 50%)",
            ]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full mb-6">
            <HelpCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Find answers to common questions about Octify's AI-powered relationship insights and features.
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          className="mb-8"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search questions..."
              className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500/30 transition-all duration-200"
            />
          </div>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          className="mb-12"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category, index) => (
              <motion.button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-200 flex items-center gap-2 ${
                  activeCategory === category.id
                    ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                    : 'bg-white/10 dark:bg-gray-800/10 text-gray-700 dark:text-gray-300 hover:bg-white/20 dark:hover:bg-gray-800/20'
                }`}
                initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
              >
                <category.icon className="w-4 h-4" />
                {category.label}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          <AnimatePresence>
            {filteredFAQs.map((faq, index) => (
              <motion.div
                key={faq.id}
                className="p-6 rounded-2xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20"
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                layout
              >
                <motion.button
                  onClick={() => toggleExpanded(faq.id)}
                  className="w-full flex items-center justify-between text-left"
                  whileHover={prefersReducedMotion ? {} : { scale: 1.01 }}
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white pr-4">
                    {faq.question}
                  </h3>
                  <motion.div
                    animate={{ rotate: expandedItems.includes(faq.id) ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                  </motion.div>
                </motion.button>

                <AnimatePresence>
                  {expandedItems.includes(faq.id) && (
                    <motion.div
                      initial={prefersReducedMotion ? {} : { opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 border-t border-white/10 dark:border-gray-700/10 mt-4">
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* No Results */}
        {filteredFAQs.length === 0 && (
          <motion.div
            className="text-center py-12"
            initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No questions found
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Try adjusting your search or browse a different category.
            </p>
            <motion.button
              onClick={onContactSupport}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full font-medium"
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
              whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
            >
              Contact Support
            </motion.button>
          </motion.div>
        )}

        {/* Contact Support */}
        <motion.div
          className="mt-12 p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20 text-center"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Still have questions?
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Our support team is here to help you get the most out of Octify.
          </p>
          <motion.button
            onClick={onContactSupport}
            className="px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full font-semibold text-lg"
            whileHover={prefersReducedMotion ? {} : { 
              scale: 1.05, 
              boxShadow: "0 20px 40px rgba(255, 107, 53, 0.3)" 
            }}
            whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
          >
            Contact Support
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};